import { UseQueryResult } from '@tanstack/react-query'
import classNames from 'classnames'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useEffect, useState } from 'react'
import { Button, OverlayTrigger, Popover } from 'react-bootstrap'
import MaterialIcon from '../../components/MaterialIcon'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { PaginatedDJResponse, Sede } from '../../types'
import { useIsAmministratore, useIsCliente, useIsOperatoreCommerciale, useIsSupervisore } from '../../utils/utils'
import ModalProposta from './ModalProposta'

type SediTableProps = {
  sedi: UseQueryResult<PaginatedDJResponse<Sede>, Error>
  setModal?: (modal: Sede) => void
  uiFilters: {
    ordering: string
  }
  setFilters: (filters: Record<string, string>) => void
  modalActionsDelete?: {
    open: (sede: Sede) => void
  }
  modalActionsUploadDVR?: {
    open: (sede: Sede) => void
  }
}

export default function SediTable({
  sedi,
  uiFilters,
  setFilters,
  setModal,
  modalActionsDelete,
  modalActionsUploadDVR,
}: SediTableProps) {
  const [openMoreSede, setOpenMoreSede] = useState<number | null>(null)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof Element && !e.target.closest(`#sede-${openMoreSede}`)) {
        setOpenMoreSede(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [openMoreSede])

  const isAdmin = useIsAmministratore()
  const isSupervisore = useIsSupervisore()
  const isCliente = useIsCliente()
  const isOperatoreCommerciale = useIsOperatoreCommerciale()
  const [sendProposalModalState, sendProposalModalActions] = useModalTrigger()

  return (
    <>
      <StickyTable className="StickyTableSmaller" isLoading={sedi.isLoading}>
        <thead className="position-sticky top-0 bg-white">
          <Sorter
            value={uiFilters.ordering}
            onSortChange={(ordering) =>
              setFilters({
                ordering,
              })
            }
          >
            <tr>
              <th>
                <div className="d-flex align-items-center">
                  Numero REA
                  <SortControl field="numero_rea" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Codice
                  <SortControl field="codice" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Via
                  <SortControl field="via" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Comune
                  <SortControl field="comune" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Provincia
                  <SortControl field="provincia" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Partita IVA
                  <SortControl field="partita_iva" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Email
                  <SortControl field="email_referente" />
                </div>
              </th>
              <th>DVR</th>
            </tr>
          </Sorter>
        </thead>
        <tbody>
          {sedi?.data?.count === 0 && (
            <tr>
              <td colSpan={8} className="text-center">
                Nessuna sede disponibile
              </td>
            </tr>
          )}
          {sedi?.data?.results?.map((sede) => (
            <tr key={sede.id}>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                <div className="d-flex align-items-center">
                  <div>
                    <div className="p-md">{sede.numero_rea}</div>
                    {sede.sede_principale && <div className="text-danger">Sede principale</div>}
                  </div>
                  <div className="ms-4  position-relative">
                    {(setModal || modalActionsDelete || modalActionsUploadDVR) && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (openMoreSede === sede.id) {
                            setOpenMoreSede(null)
                          } else {
                            setOpenMoreSede(sede.id)
                          }
                        }}
                        variant="light"
                        className="text-secondary d-flex align-items-center"
                        type="button"
                      >
                        <span className="material-icons">more_horiz</span>
                      </Button>
                    )}
                    {openMoreSede === sede.id && (
                      <div
                        id={`sede-${sede.id}`}
                        className="list-group position-absolute"
                        // ref={editDeleteRef}
                        style={{
                          zIndex: 1000,
                          width: '200px',
                          boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',

                          borderRadius: '5px',
                        }}
                      >
                        {(isAdmin || isSupervisore || isOperatoreCommerciale) && (
                          <button
                            onClick={() => {
                              sendProposalModalActions.open(sede)
                            }}
                            className="list-group-item list-group-item-action d-flex align-items-center"
                          >
                            <span
                              className="material-icons me-2"
                              style={{
                                fontSize: '16px',
                              }}
                            >
                              send
                            </span>
                            Invia proposta
                          </button>
                        )}
                        {!isCliente && (
                          <button
                            onClick={() => {
                              setModal?.(sede)
                            }}
                            className="list-group-item list-group-item-action d-flex align-items-center"
                          >
                            <span
                              className="material-icons me-2"
                              style={{
                                fontSize: '16px',
                              }}
                            >
                              edit
                            </span>
                            Modifica
                          </button>
                        )}
                        {(isAdmin || isSupervisore) && sede.can_delete && (
                          <button
                            onClick={() => {
                              modalActionsDelete?.open(sede)
                            }}
                            className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                          >
                            <span
                              className="material-icons me-2"
                              style={{
                                fontSize: '16px',
                              }}
                            >
                              delete
                            </span>
                            Elimina
                          </button>
                        )}
                        {isCliente && (
                          <button
                            onClick={() => {
                              modalActionsUploadDVR?.open(sede)
                            }}
                            className="list-group-item list-group-item-action d-flex align-items-center"
                          >
                            <span
                              className="material-icons me-2"
                              style={{
                                fontSize: '16px',
                              }}
                            >
                              upload
                            </span>
                            Carica DVR
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {sede.codice}
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {sede.toponimo_data?.nome} {sede.via}
                {', '}
                {sede.civico}
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {sede.comune_data?.nome}
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {sede.provincia_data?.sigla}
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {sede.partita_iva}
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {sede.email_referente}
              </td>
              <td
                className={classNames({
                  'bg-orange-light': sede.sede_principale,
                })}
              >
                {typeof sede.dvr === 'string' ? (
                  <a href={sede.dvr} target="_blank" rel="noopener noreferrer">
                    <MaterialIcon
                      icon="file_open"
                      fontSize={20}
                      className="material-icons-outlined"
                      title="Visualizza DVR"
                    />
                  </a>
                ) : (
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Popover id="popover-basic">
                        <Popover.Body>
                          <div>DVR assente</div>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <div>
                      <MaterialIcon
                        icon="priority_high"
                        fontSize={20}
                        className="material-icons-outlined text-danger"
                        title="DVR assente"
                      />
                    </div>
                  </OverlayTrigger>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </StickyTable>
      {sendProposalModalState.value && (
        <ModalProposta
          isOpen={sendProposalModalState.isOpen}
          toggle={sendProposalModalActions.close}
          cliente={sendProposalModalState.value.cliente}
          sede={sendProposalModalState.value}
        />
      )}
    </>
  )
}
